import { useState, useCallback } from 'react';

/**
 * Generic hook for managing compliance thresholds
 * Provides validation and state management for threshold configurations
 * Thresholds are temporary and reset to team defaults on page refresh
 */
export interface ThresholdDefinition {
  key: string;
  label: string;
  defaultValue: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string; // e.g., '%', 'count', 'KB'
  description?: string;
}

export interface ThresholdConfig {
  [key: string]: number;
}

export type ThresholdType = 'structure';

export interface ComplianceThresholdsOptions {
  type: ThresholdType;
  onThresholdChange?: (thresholds: ThresholdConfig) => void;
}

/**
 * Centralized threshold definitions by type
 */
const getThresholdDefinitions = (type: ThresholdType): ThresholdDefinition[] => {
  switch (type) {
    case 'structure':
      return [
        {
          key: 'fileSize',
          label: 'File Size Threshold (%)',
          defaultValue: 5,
          min: 0.1,
          max: 50,
          step: 0.1,
          unit: '%',
          description: 'Acceptable percentage difference in file size'
        },
        {
          key: 'rowCount',
          label: 'Row Count Threshold (%)',
          defaultValue: 2,
          min: 0.1,
          max: 50,
          step: 0.1,
          unit: '%',
          description: 'Acceptable percentage difference in row count'
        }
      ];
    default:
      return [];
  }
};

export const useComplianceThresholds = ({
  type,
  onThresholdChange
}: ComplianceThresholdsOptions) => {
  
  // Get predefined definitions based on type
  const definitions = getThresholdDefinitions(type);
  
  // Create default thresholds from definitions (always use team defaults)
  const defaults = definitions.reduce((acc, def) => {
    acc[def.key] = def.defaultValue;
    return acc;
  }, {} as ThresholdConfig);

  // Initialize with team defaults - no persistence
  const [thresholds, setThresholds] = useState<ThresholdConfig>(defaults);
  const [showThresholds, setShowThresholds] = useState(false);

  const updateThreshold = useCallback((key: string, value: number) => {
    const definition = definitions.find(def => def.key === key);
    if (!definition) return;

    // Validate against min/max
    let validatedValue = value;
    if (definition.min !== undefined && value < definition.min) {
      validatedValue = definition.min;
    }
    if (definition.max !== undefined && value > definition.max) {
      validatedValue = definition.max;
    }

    setThresholds(prev => {
      const newThresholds = { ...prev, [key]: validatedValue };
      
      // Call onChange callback for real-time updates
      if (onThresholdChange) {
        onThresholdChange(newThresholds);
      }
      
      return newThresholds;
    });
  }, [definitions, onThresholdChange]);

  const resetToDefaults = useCallback(() => {
    setThresholds(defaults);
    
    if (onThresholdChange) {
      onThresholdChange(defaults);
    }
  }, [defaults, onThresholdChange]);

  const isWithinThreshold = useCallback((key: string, value: number): boolean => {
    const threshold = thresholds[key];
    if (threshold === undefined) return true;
    
    const definition = definitions.find(def => def.key === key);
    if (!definition) return true;
    
    // Default behavior: check if absolute value is within threshold
    return Math.abs(value) <= threshold;
  }, [thresholds, definitions]);

  const isOutsideThreshold = useCallback((key: string, value: number): boolean => {
    return !isWithinThreshold(key, value);
  }, [isWithinThreshold]);

  return {
    thresholds,
    showThresholds,
    setShowThresholds,
    updateThreshold,
    resetToDefaults,
    isWithinThreshold,
    isOutsideThreshold,
    definitions
  };
};
